/* Independently authored Katlayan section and block behavior. No remote theme runtime. */
(() => {
  const Katlayan = window.Katlayan = window.Katlayan || {};
  if (Katlayan.content) return;
  const mounted = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const tablet = window.matchMedia('(min-width: 768px)');
  const all = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const number = (value, fallback) => Number.isFinite(Number(value)) && value !== '' ? Number(value) : fallback;
  const direct = (root, selector) => [...root.children].filter(node => node.matches(selector));
  const cleanClone = source => {
    const node = source.cloneNode(true);
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('inert', '');
    for (const item of [node, ...node.querySelectorAll('*')]) {
      item.removeAttribute('id');
      item.removeAttribute('data-shopify-editor-block');
      item.removeAttribute('data-shopify-editor-section');
      if (item.matches('a,button,input,select,textarea,[tabindex]')) item.setAttribute('tabindex', '-1');
    }
    return node;
  };
  function own(element, create) {
    if (mounted.has(element)) return;
    const abort = new AbortController();
    const disposals = [];
    const listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: abort.signal });
    const dispose = fn => disposals.push(fn);
    try {
      create({ listen, dispose, signal: abort.signal });
      mounted.set(element, () => { abort.abort(); for (const fn of disposals.reverse()) fn(); });
    } catch (error) {
      abort.abort();
      for (const fn of disposals.reverse()) fn();
      console.error('Katlayan içerik alanı başlatılamadı.', error);
    }
  }
  function marquee(element) {
    own(element, ({ listen, dispose }) => {
      const track = element.querySelector('.kc-marquee-track');
      const set = track?.querySelector('[data-kc-marquee-set]');
      if (!set || !set.children.length) return;
      let copies = [];
      const resize = () => {
        for (const copy of copies) copy.remove();
        copies = [];
        const distance = set.getBoundingClientRect().width;
        if (distance <= 0) return;
        const count = Math.max(1, Math.ceil(element.clientWidth / distance));
        for (let index = 0; index < count; index++) {
          const copy = cleanClone(set);
          copy.removeAttribute('data-kc-marquee-set');
          track.append(copy);
          copies.push(copy);
        }
        track.style.setProperty('--kc-marquee-distance', `${distance}px`);
        track.style.setProperty('--kc-marquee-duration', `${distance / Math.max(1, number(element.dataset.speed, 30))}s`);
        track.style.setProperty('--kc-marquee-direction', element.dataset.direction === 'reverse' ? 'reverse' : 'normal');
      };
      const observer = new ResizeObserver(resize);
      observer.observe(element);
      observer.observe(set);
      dispose(() => observer.disconnect());
      dispose(() => copies.forEach(copy => copy.remove()));
      listen(element, 'pointerenter', () => {
        if (element.dataset.pauseHover !== 'false') track.style.animationPlayState = 'paused';
      });
      listen(element, 'pointerleave', () => { track.style.animationPlayState = ''; });
      listen(element, 'focusin', () => { track.style.animationPlayState = 'paused'; });
      listen(element, 'focusout', event => {
        if (!element.contains(event.relatedTarget)) track.style.animationPlayState = '';
      });
      resize();
    });
  }
  function carousel(element) {
    own(element, ({ listen, dispose }) => {
      const track = direct(element, '[data-kc-track]')[0];
      if (!track) return;
      const slides = [...track.children];
      const controls = direct(element, '.kc-carousel-controls')[0];
      const dots = controls?.querySelector('[data-kc-dots]');
      let visible = 1;
      let selected = 0;
      let timer = null;
      let scrollFrame = 0;
      const max = () => Math.max(0, slides.length - visible);
      const step = () => slides.length > 1 ? Math.abs(slides[1].offsetLeft - slides[0].offsetLeft) : track.clientWidth;
      const syncDots = () => {
        for (const [index, dot] of [...(dots?.children || [])].entries()) dot.setAttribute('aria-current', String(index === selected));
      };
      const go = (index, smooth = true) => {
        selected = index < 0 ? max() : index > max() ? 0 : index;
        track.scrollTo({ left: selected * step(), behavior: smooth && !reduced.matches ? 'smooth' : 'instant' });
        syncDots();
      };
      const stop = () => { if (timer) clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        if (element.dataset.autoplay === 'true' && !reduced.matches && max() > 0) timer = setInterval(() => go(selected + 1), Math.max(1000, number(element.dataset.autoplaySpeed, 5) * 1000));
      };
      const resize = () => {
        visible = Math.max(1, Math.min(slides.length || 1, number(desktop.matches ? element.dataset.slidesDesktop : tablet.matches ? element.dataset.slidesTablet : element.dataset.slidesMobile, 1)));
        element.style.setProperty('--kc-visible', visible);
        element.dataset.kcStatic = String(max() === 0);
        if (dots) {
          dots.replaceChildren();
          for (let index = 0; index <= max(); index++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', `${index + 1}. karta git`);
            dot.dataset.kcDot = index;
            dots.append(dot);
          }
        }
        selected = Math.min(selected, max());
        go(selected, false);
        start();
      };
      if (controls) listen(controls, 'click', event => {
        if (event.target.closest('[data-kc-prev]')) go(selected - 1);
        else if (event.target.closest('[data-kc-next]')) go(selected + 1);
        else if (event.target.closest('[data-kc-dot]')) go(number(event.target.closest('[data-kc-dot]').dataset.kcDot, 0));
      });
      listen(track, 'scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          selected = Math.min(max(), Math.max(0, Math.round(track.scrollLeft / Math.max(1, step()))));
          syncDots();
        });
      }, { passive: true });
      listen(element, 'pointerenter', stop);
      listen(element, 'pointerleave', start);
      listen(element, 'focusin', stop);
      listen(element, 'focusout', event => { if (!element.contains(event.relatedTarget)) start(); });
      listen(document, 'visibilitychange', () => document.hidden ? stop() : start());
      listen(desktop, 'change', resize);
      listen(tablet, 'change', resize);
      listen(reduced, 'change', resize);
      const observer = new ResizeObserver(() => go(Math.min(selected, max()), false));
      observer.observe(track);
      dispose(() => observer.disconnect());
      dispose(stop);
      dispose(() => cancelAnimationFrame(scrollFrame));
      dispose(() => { if (dots) dots.replaceChildren(); });
      element.kcRevealSlide = target => {
        const index = slides.findIndex(slide => slide === target || slide.contains(target));
        if (index >= 0) go(Math.min(index, max()), false);
      };
      dispose(() => delete element.kcRevealSlide);
      resize();
    });
  }
  function reviews(section) {
    own(section, ({ dispose }) => {
      const source = section.querySelector('[data-kc-review-source]');
      if (!source) return;
      const children = [...source.children];
      const images = children.filter(node => node.matches('.kc-image'));
      const cards = children.filter(node => node.matches('.kc-column'));
      const additions = [];
      if (images.length) {
        const belt = document.createElement('div');
        belt.className = 'kc-marquee kc-review-marquee';
        belt.dataset.kcMarquee = '';
        belt.dataset.speed = section.dataset.marqueeSpeed || '30';
        belt.dataset.pauseHover = String(desktop.matches);
        const track = document.createElement('div');
        track.className = 'kc-marquee-track';
        const set = document.createElement('div');
        set.className = 'kc-marquee-set';
        set.dataset.kcMarqueeSet = '';
        set.append(...images);
        track.append(set);
        belt.append(track);
        source.before(belt);
        additions.push(belt);
        marquee(belt);
      }
      if (cards.length) {
        const slider = document.createElement('div');
        slider.className = 'kc-slider kc-review-carousel';
        slider.dataset.kcCarousel = '';
        slider.dataset.kcCarouselKind = 'reviews';
        slider.dataset.slidesDesktop = '3';
        slider.dataset.slidesTablet = '2';
        slider.dataset.slidesMobile = '1';
        const track = document.createElement('div');
        track.className = 'kc-carousel-track';
        track.dataset.kcTrack = '';
        track.append(...cards);
        const controls = document.createElement('div');
        controls.className = 'kc-carousel-controls';
        controls.innerHTML = '<button type="button" data-kc-prev aria-label="Önceki kart">‹</button><div data-kc-dots></div><button type="button" data-kc-next aria-label="Sonraki kart">›</button>';
        slider.append(track, controls);
        source.before(slider);
        additions.push(slider);
        for (const card of cards) {
          const image = direct(card, '.kc-card-image')[0];
          if (!image) continue;
          const footer = document.createElement('div');
          footer.className = 'kc-review-footer';
          const label = document.createElement('span');
          label.textContent = section.dataset.kcCardFooterLabel?.trim() || section.querySelector('.kc-section-heading h2')?.textContent.trim() || 'Ürün ayrıntıları';
          footer.append(image, label);
          card.append(footer);
          dispose(() => { card.prepend(image); footer.remove(); });
        }
        carousel(slider);
      }
      source.hidden = true;
      dispose(() => {
        for (const addition of additions) unmount(addition);
        source.append(...children);
        source.hidden = false;
        additions.forEach(node => node.remove());
      });
    });
  }
  function tabs(section) {
    own(section, ({ listen, dispose }) => {
      const nav = section.querySelector('[data-kc-tab-navigation]');
      const panels = [...section.querySelectorAll('[data-kc-tabs] > [data-kc-tab]')];
      if (!nav || !panels.length) return;
      const buttons = panels.map((panel, index) => {
        const button = document.createElement('button');
        const id = `${section.id}-tab-${index}`;
        button.type = 'button';
        button.id = id;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', `${id}-panel`);
        button.textContent = panel.dataset.kcTabLabel;
        button.dataset.kcTabIndex = index;
        panel.id = `${id}-panel`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', id);
        nav.append(button);
        return button;
      });
      const show = index => {
        panels.forEach((panel, i) => {
          panel.hidden = i !== index;
          panel.querySelectorAll('video').forEach(video => { if (i !== index) video.pause(); });
          buttons[i].setAttribute('aria-selected', String(i === index));
          buttons[i].tabIndex = i === index ? 0 : -1;
        });
      };
      listen(nav, 'click', event => {
        const button = event.target.closest('[data-kc-tab-index]');
        if (button) show(number(button.dataset.kcTabIndex, 0));
      });
      listen(nav, 'keydown', event => {
        const index = buttons.indexOf(document.activeElement);
        if (index < 0) return;
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') next = (index + buttons.length - 1) % buttons.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = buttons.length - 1;
        else return;
        event.preventDefault();
        show(next);
        buttons[next].focus();
      });
      section.kcRevealTab = target => {
        const index = panels.findIndex(panel => panel === target || panel.contains(target));
        if (index >= 0) show(index);
      };
      dispose(() => {
        nav.replaceChildren();
        panels.forEach(panel => { panel.hidden = false; panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); });
        delete section.kcRevealTab;
      });
      show(0);
    });
  }
  function flip(element) {
    own(element, ({ listen, dispose }) => {
      const front = element.querySelector('.kc-flip-front');
      const back = element.querySelector('.kc-flip-back');
      const toggles = [...element.querySelectorAll('[data-kc-flip-toggle]')];
      const show = active => {
        element.classList.toggle('is-flipped', active);
        front?.setAttribute('aria-hidden', String(active));
        back?.setAttribute('aria-hidden', String(!active));
        if (front) front.inert = active;
        if (back) back.inert = !active;
        toggles.forEach(button => button.setAttribute('aria-expanded', String(active)));
      };
      listen(element, 'click', event => {
        const button = event.target.closest('[data-kc-flip-toggle]');
        if (!button) return;
        const next = !element.classList.contains('is-flipped');
        show(next);
        (next ? back : front)?.querySelector('button')?.focus({ preventScroll: true });
      });
      listen(element, 'keydown', event => {
        if (event.key === 'Escape' && element.classList.contains('is-flipped')) {
          show(false);
          front?.querySelector('button')?.focus({ preventScroll: true });
        }
      });
      dispose(() => show(false));
      show(false);
    });
  }
  function counters(section) {
    own(section, ({ dispose, listen }) => {
      const rows = [...section.querySelectorAll('[data-kc-result-row]')];
      const steps = section.dataset.resultMode === 'steps';
      const duration = Math.max(0, number(section.dataset.counterDuration, 1800));
      const delay = Math.max(0, number(section.dataset.barDelay, 1000));
      const frames = new Set();
      const timers = new Set();
      const animate = (node, from, to, ms, update) => {
        const start = performance.now();
        const tick = now => {
          const progress = ms === 0 ? 1 : Math.min(1, (now - start) / ms);
          update(from + (to - from) * progress);
          if (progress < 1) { const frame = requestAnimationFrame(tick); frames.add(frame); }
        };
        const frame = requestAnimationFrame(tick); frames.add(frame);
      };
      const finish = row => {
        const count = row.querySelector('[data-kc-counter]');
        const progress = row.querySelector('[data-kc-progress]');
        count.textContent = String(steps ? number(count.dataset.step, 1) : number(count.dataset.target, 0));
        progress.style.width = `${Math.max(0, Math.min(100, number(progress.dataset.progress, 0)))}%`;
      };
      const run = row => {
        if (row.dataset.kcCounted) return;
        row.dataset.kcCounted = 'true';
        if (reduced.matches || document.documentElement.classList.contains('shopify-design-mode')) { finish(row); return; }
        const count = row.querySelector('[data-kc-counter]');
        const progress = row.querySelector('[data-kc-progress]');
        const target = steps ? number(count.dataset.step, 1) : number(count.dataset.target, 0);
        animate(count, 0, target, duration, value => { count.textContent = Math.round(value).toString(); });
        const timer = setTimeout(() => {
          timers.delete(timer);
          animate(progress, 0, Math.max(0, Math.min(100, number(progress.dataset.progress, 0))), duration, value => { progress.style.width = `${value}%`; });
        }, delay);
        timers.add(timer);
      };
      rows.forEach(row => {
        const count = row.querySelector('[data-kc-counter]');
        const suffix = row.querySelector('[data-kc-counter-suffix]');
        if (suffix) suffix.hidden = steps;
        if (reduced.matches) finish(row);
        else { count.textContent = '0'; row.querySelector('[data-kc-progress]').style.width = '0%'; }
      });
      const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { run(entry.target); observer.unobserve(entry.target); } }), { threshold: .1 });
      rows.forEach(row => observer.observe(row));
      listen(reduced, 'change', () => { if (reduced.matches) rows.forEach(finish); });
      dispose(() => {
        observer.disconnect();
        frames.forEach(cancelAnimationFrame);
        timers.forEach(clearTimeout);
        rows.forEach(row => { delete row.dataset.kcCounted; finish(row); });
      });
    });
  }
  function typing(section) {
    if (section.dataset.typing !== 'true' || !section.dataset.typingSuffix) return;
    own(section, ({ dispose, listen }) => {
      const heading = section.querySelector('.kc-columns-grid > .kc-column:first-child [data-kc-heading]');
      if (!heading) return;
      const suffix = document.createElement('span');
      suffix.className = 'kc-typewriter-suffix';
      suffix.setAttribute('aria-hidden', 'true');
      const caret = document.createElement('span');
      caret.className = 'kc-typewriter-caret';
      caret.setAttribute('aria-hidden', 'true');
      caret.textContent = '|';
      const accessible = document.createElement('span');
      accessible.className = 'kc-visually-hidden';
      accessible.textContent = section.dataset.typingSuffix;
      heading.append(suffix, caret, accessible);
      const value = section.dataset.typingSuffix;
      let position = 0;
      let deleting = false;
      let timer;
      const tick = () => {
        if (reduced.matches) { suffix.textContent = value; caret.hidden = true; return; }
        caret.hidden = false;
        position += deleting ? -1 : 1;
        suffix.textContent = value.slice(0, position);
        let wait = number(deleting ? section.dataset.typingDelete : section.dataset.typingSpeed, deleting ? 40 : 80);
        if (position >= value.length) { deleting = true; wait = number(section.dataset.typingPause, 3000); }
        else if (position <= 0) { deleting = false; wait = number(section.dataset.typingRestart, 800); }
        timer = setTimeout(tick, Math.max(10, wait));
      };
      listen(reduced, 'change', () => { clearTimeout(timer); tick(); });
      dispose(() => { clearTimeout(timer); suffix.remove(); caret.remove(); accessible.remove(); });
      tick();
    });
  }
  function timeline(element) {
    own(element, ({ dispose }) => {
      const items = [...element.querySelectorAll('[data-kc-timeline-item]')];
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-current'); observer.unobserve(entry.target); }
      }), { rootMargin: '0px 0px -20% 0px', threshold: .1 });
      items.forEach(item => observer.observe(item));
      dispose(() => observer.disconnect());
    });
  }
  function video(element) {
    own(element, ({ listen, dispose }) => {
      const videos = [...element.querySelectorAll('video')];
      if (element.closest('.kc-columns-grid[data-sales-layout="sp-video-lists"]')) {
        const states = new Map(videos.map(item => [item, { autoplay: item.autoplay && item.muted, visible: false, pausedByUser: false, internalPauses: 0 }]));
        const pause = item => {
          if (!item.paused) { states.get(item).internalPauses++; item.pause(); }
        };
        const resume = item => {
          const state = states.get(item);
          if (state.autoplay && state.visible && !state.pausedByUser && !reduced.matches && item.isConnected && item.paused) item.play().catch(() => {});
        };
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
          states.get(entry.target).visible = entry.isIntersecting;
          if (entry.isIntersecting) resume(entry.target);
          else pause(entry.target);
        }), { threshold: 0 });
        for (const item of videos) {
          const state = states.get(item);
          listen(item, 'pause', () => {
            if (state.internalPauses) state.internalPauses--;
            else if (state.autoplay) state.pausedByUser = true;
          });
          listen(item, 'play', () => {
            state.pausedByUser = false;
            if (state.autoplay && !state.visible) { pause(item); return; }
            document.querySelectorAll('video').forEach(other => { if (other !== item) other.pause(); });
          });
          observer.observe(item);
          if (reduced.matches && item.autoplay) { item.autoplay = false; pause(item); }
        }
        listen(reduced, 'change', () => videos.forEach(item => {
          if (reduced.matches) {
            if (item.autoplay) item.autoplay = false;
            if (states.get(item).autoplay) pause(item);
          } else resume(item);
        }));
        dispose(() => { observer.disconnect(); videos.forEach(item => item.pause()); });
        return;
      }
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) entry.target.pause();
      }), { threshold: 0 });
      for (const item of videos) {
        observer.observe(item);
        if (reduced.matches && item.autoplay) { item.autoplay = false; item.pause(); }
        listen(item, 'play', () => document.querySelectorAll('video').forEach(other => { if (other !== item) other.pause(); }));
      }
      dispose(() => { observer.disconnect(); videos.forEach(item => item.pause()); });
    });
  }
  function init(root = document) {
    all(root, '[data-kc-content]').forEach(section => {
      if (section.dataset.kcKind === 'reviews') reviews(section);
      if (section.dataset.kcKind === 'tabs') tabs(section);
      if (section.dataset.kcKind === 'results') counters(section);
      if (section.dataset.kcKind === 'columns') typing(section);
    });
    all(root, '[data-kc-marquee]').forEach(marquee);
    all(root, '[data-kc-carousel]').forEach(carousel);
    all(root, '[data-kc-flip]').forEach(flip);
    all(root, '[data-kc-timeline]').forEach(timeline);
    all(root, '[data-kc-video]').forEach(video);
  }
  function unmount(root) {
    for (const [element, cleanup] of [...mounted]) if (element === root || root.contains(element)) {
      mounted.delete(element);
      cleanup();
    }
  }
  Katlayan.content = { init, destroy: unmount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  document.addEventListener('shopify:block:select', event => {
    const id = event.detail?.blockId;
    const target = event.target.matches?.('[data-shopify-editor-block]') ? event.target : id ? document.querySelector(`[data-shopify-editor-block*='"id":"${CSS.escape(id)}"']`) : event.target;
    if (!target) return;
    target.closest('[data-kc-carousel]')?.kcRevealSlide?.(target);
    target.closest('[data-kc-kind="tabs"]')?.kcRevealTab?.(target);
    const faq = target.closest('[data-kc-faq-row]');
    if (faq) faq.open = true;
    const timelineItem = target.closest('[data-kc-timeline-item]');
    if (timelineItem) timelineItem.classList.add('is-current');
  });
})();
